import { globals } from 'common-ui';
import { getQueryFilters, typeaheadCheck, resultsCustomConfig } from '../utils';
import { getAlgoliaResults } from '@algolia/autocomplete-js';
import { productLinkBuilder } from 'common-next/src/components/common-functions';
import { ProductItem } from '../productCard/productItem';
import type { GetSourcesParams, OnSelectParams } from '@algolia/autocomplete-core';
import type { SearchClient } from 'algoliasearch/lite';
import type { BaseItem, ExtendedAlgoliaSearchResult } from './types';
import { storeQueryIDForProduct } from '../../../utils';

interface GetItemsParams {
  setContext: (context: any) => void;
  refresh: () => void;
  query: string;
}

const templates: {
  header: () => React.JSX.Element;
  item: ({ item, components }: any) => React.JSX.Element;
  noResults?: () => string;
} = {
  header() {
    return <span className="aa-SourceHeaderTitle">Products</span>;
  },
  item({ item, components }: any) {
    return <ProductItem hit={item} components={components} />;
  },
};
// Conditionally add `noResults`
if (!globals?.algoliaNewView) {
  templates.noResults = () => {
    if (globals?.algoliaNewView) {
      return '';
    }
    return 'No products for this query.';
  };
}

const ProductPluginHeader = () => <span className="aa-SourceHeaderTitle">Products</span>;
const ProductPluginItem = ({ item, components }: any) => <ProductItem hit={item} components={components} />;

export function transformResponseHandler(
  { hits, results }: any,
  setContext: (ctx: any) => void,
  refresh: () => void,
  currentContext: any, // <- Accept currentContext to compare changes
) {
  let products = (hits?.[0] || []) as ExtendedAlgoliaSearchResult[];

  if (globals?.algoliaNewView && products.length > 1) {
    products = products.slice(1);
  }

  const [firstHit] = products;

  const updatedContext: any = firstHit
    ? {
        skuItemCodes: firstHit.skuItemCodes || [],
        productName: firstHit.productName || '',
        vintage: firstHit.vintage || '',
        giftFlag: firstHit.giftFlag || false,
      }
    : {
        skuItemCodes: [],
        productName: '',
        vintage: '',
        giftFlag: false,
      };

  // Include queryID and noResultsFound in context
  updatedContext.queryID = results[0].queryID;
  updatedContext.noResultsFound = results[0].hits.length === 0;
  const [topHit] = results[0].hits;
  updatedContext.topHit = topHit;

  const queryIDChanged = currentContext?.queryID !== updatedContext.queryID;
  const noResultsFoundChanged = currentContext?.noResultsFound !== updatedContext.noResultsFound;

  if (queryIDChanged || noResultsFoundChanged) {
    setTimeout(() => {
      setContext(updatedContext);
      if (globals?.algoliaNewView === true) {
        refresh();
      }
    }, 0);
  }

  const limit = resultsCustomConfig().hitsPerPage;
  return !globals?.algoliaNewView && products.length > limit ? products.slice(0, limit) : products;
}

export function createProductsPlugin(
  searchClient: SearchClient,
  currentState: string,
): { getSources: (params: GetSourcesParams<BaseItem>) => any } {
  return {
    getSources({ query: sourceQuery, state }: GetSourcesParams<BaseItem>) {
      if (!sourceQuery || !typeaheadCheck(sourceQuery)) {
        return [];
      }

      return [
        {
          sourceId: 'products',
          getItems({ setContext, refresh, query: searchQuery }: GetItemsParams) {
            return getAlgoliaResults({
              searchClient,
              queries: [
                {
                  indexName: globals.algoliaIndexName as string,
                  query: searchQuery,
                  params: {
                    clickAnalytics: true,
                    hitsPerPage: resultsCustomConfig().hitsPerPage,
                    attributesToSnippet: ['name:10', 'Description:20'],
                    snippetEllipsisText: '…',
                    filters: getQueryFilters(currentState),
                  },
                },
              ],
              transformResponse: response => transformResponseHandler(response, setContext, refresh, state.context),
            });
          },
          onSelect({ item, state: selectState }: OnSelectParams<BaseItem>) {
            storeQueryIDForProduct(
              item?.itemCode as string,
              selectState.context.queryID as string,
              0,
              item.__autocomplete_indexName as string,
            );
            window.location.href = productLinkBuilder(
              item?.productName as string,
              item?.vintage as string,
              item?.itemCode as string,
            );
          },
          templates: {
            header: ProductPluginHeader,
            item: ProductPluginItem,
            ...(globals?.algoliaNewView === false ? { noResults: templates.noResults } : {}),
          },
        },
      ];
    },
  };
}
